import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { RepScheme } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faSave,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import {
  ConfigProvider,
  Empty,
  Form,
  GetProp,
  Input,
  Select,
  Space,
  Table,
} from "antd";
import IconButton from "../../common/IconButton";
import { Option, PagedResults } from "../../types/generics";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function RepSchemesList() {
  const { showToast } = useToasts();
  const [repSchemes, setRepSchemes] = useState<RepScheme[]>();
  const [repSchemeTypes, setRepSchemeTypes] = useState<Option[]>([]);
  const [error, setError] = useState(false);
  const [totalRepSchemes, setTotalRepSchemes] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    repSchemeTypeId?: number;
    pageSize: number;
    currentPage: number;
  }>({
    pageSize: 20,
    currentPage: 1,
  });
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form] = Form.useForm();
  const [selectedItem, setSelectedItem] = useState<RepScheme>();

  // 🔹 Inizializza lista RepSchemes
  const initRepSchemes = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);

    if (filters.repSchemeTypeId)
      urlParams.append("repSchemeTypeId", `${filters.repSchemeTypeId}`);

    Entities.getPaginatedRepSchemes(urlParams).then(
      ({ data }) => {
        setRepSchemes((data as PagedResults<RepScheme>).items);
        setTotalRepSchemes((data as PagedResults<RepScheme>).totalItems);
      },
      () => {
        showToast(
          "repscheme-error",
          "Impossibile ottenere la lista dei Rep Scheme",
          "",
          "danger"
        );
        setRepSchemes([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  // 🔹 Inizializza RepSchemeType per il Select
  const initRepSchemeTypes = useCallback(() => {
    Entities.getRepSchemeTypes().then(
      ({ data }) => {
        setRepSchemeTypes(
          data.list.map((repSchemeType) => ({
            label: repSchemeType.repSchemeType,
            value: repSchemeType.id,
          }))
        );
      },
      () => {
        showToast(
          "repstype-error",
          "Impossibile ottenere la lista dei tipi di Rep Scheme",
          "",
          "danger"
        );
        setRepSchemeTypes([]);
      }
    );
  }, [showToast]);

  const reset = useCallback(() => {
    setError(false);
    setRepSchemes(undefined);
    initRepSchemes();
    initRepSchemeTypes();
  }, [initRepSchemes, initRepSchemeTypes]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  }, []);

  const handleEditClick = useCallback(
    (item?: RepScheme) => () => {
      setSelectedItem(item);
      if (item) {
        form.setFieldsValue({
          repScheme: item.repScheme,
          repSchemeTypeId: item.repSchemeTypeId,
        });
      }
      setAddModal(true);
    },
    [form]
  );

  const handleDeleteClick = useCallback(
    (item: RepScheme) => () => {
      setSelectedItem(item);
      setDeleteModal(true);
    },
    []
  );

  const handleFieldChange = useCallback(
    (field: string) => (evt: any) => {
      setFilters((previous) => ({
        ...previous,
        [field]: evt,
      }));
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Rep Scheme"
        count={totalRepSchemes}
        actions={
          <button onClick={handleEditClick()} className="button-primary">
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Aggiungi Rep Scheme</span>
          </button>
        }
      >
        <Select
          value={filters.repSchemeTypeId}
          placeholder="Cerca per Rep Scheme Type..."
          allowClear
          onChange={handleFieldChange("repSchemeTypeId")}
        >
          {(repSchemeTypes || []).map((repSchemeType, index) => (
            <Select.Option
              key={index}
              value={repSchemeType.value}
              label={repSchemeType.label}
            >
              <span>{repSchemeType.label}</span>
            </Select.Option>
          ))}
        </Select>
      </ListPageHeader>
    );
  }, [
    handleEditClick,
    filters,
    handleFieldChange,
    repSchemeTypes,
    totalRepSchemes,
  ]);

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="Nessun Rep Scheme"
    />
  );

  const renderTable = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi.
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        dataSource={repSchemes}
        rowKey="id"
        locale={{ emptyText: renderEmpty() }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalRepSchemes,
        }}
      >
        <Table.Column
          key="repScheme"
          dataIndex="repScheme"
          title="Rep Scheme"
          className="text-center col-pattern"
        />
        <Table.Column
          key="repSchemeType"
          title="Tipo"
          className="text-center col-pattern"
          render={(_value, record: RepScheme) => (
            <span>{record.repSchemeType?.repSchemeType || "-"}</span>
          )}
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: RepScheme) => (
            <Space size={1}>
              <IconButton tooltip="Modifica" onClick={handleEditClick(record)}>
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    repSchemes,
    error,
    filters,
    totalRepSchemes,
    handlePageChange,
    handlePageSizeChange,
    handleEditClick,
    handleDeleteClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return <ListPanel loading={!repSchemes}>{renderTable()}</ListPanel>;
  }, [repSchemes, renderTable]);

  const handleAddModalClose = useCallback(() => {
    setAddModal(false);
    form.resetFields();
    setSelectedItem(undefined);
  }, [form]);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModal(false);
    setSelectedItem(undefined);
  }, []);

  const handleSubmit = useCallback(
    (values: any) => {
      setLoading(true);
      const body = { ...values };
      const promise = selectedItem
        ? Entities.updateRepScheme(selectedItem.id!, body)
        : Entities.createRepScheme(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "repscheme-success",
            `Rep Scheme ${
              selectedItem ? "modificato" : "creato"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "repscheme-error",
            `Errore nella ${
              selectedItem ? "modifica" : "creazione"
            } del Rep Scheme`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedItem, showToast, handleAddModalClose, reset]
  );

  const handleDelete = useCallback(() => {
    setLoading(true);
    Entities.deleteRepScheme(selectedItem!.id!).then(
      () => {
        setLoading(false);
        handleDeleteModalClose();
        reset();
        showToast(
          "delete-repscheme-success",
          "Rep Scheme eliminato correttamente",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-repscheme-error",
          "Errore nella cancellazione del Rep Scheme, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [selectedItem, showToast, handleDeleteModalClose, reset]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      {/* MODALE AGGIUNTA / MODIFICA */}
      <Modal show={addModal} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedItem ? "Modifica" : "Nuovo"} Rep Scheme
          </Modal.Title>
        </Modal.Header>
        <Form form={form} onFinish={handleSubmit}>
          <Modal.Body>
            <Form.Item
              label="Rep Scheme"
              name="repScheme"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Input className="w-100" />
            </Form.Item>
            <Form.Item
              label="Rep Scheme Type"
              name="repSchemeTypeId"
              rules={[{ required: true, message: "Campo obbligatorio" }]}
            >
              <Select placeholder="Seleziona un Rep Scheme Type">
                {repSchemeTypes.map((type) => (
                  <Select.Option key={type.value} value={type.value}>
                    {type.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button
              onClick={handleAddModalClose}
              variant="light"
              size="sm"
              className="border me-2"
            >
              Annulla
            </Button>
            <Button variant="success" size="sm" type="submit">
              {loading ? (
                <TailSpin width={15} height={15} color="#9c1419" />
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} />
                  <span className="ms-2">Salva</span>
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* MODALE ELIMINAZIONE */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Rep Scheme</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare il Rep Scheme{" "}
            <strong>{selectedItem?.repScheme}</strong>?
          </p>
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDelete}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
